import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { CrestLogo } from "../components/CrestLogo";
import { LOGIN } from "../constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // Store user info in localStorage or session (HttpOnly cookie does the heavy lifting)
      localStorage.setItem("adminUser", JSON.stringify(response.data));
      
      // Redirect to Admin Dashboard
      navigate("/admin");
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.detail;
      setError(formatApiErrorDetail(errMsg) || "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F7F5F2] min-h-screen flex flex-col justify-between selection:bg-[#1F3A33] selection:text-[#F7F5F2]">
      {/* Header */}
      <header className="h-20 flex items-center border-b border-[#1F3A33]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <CrestLogo variant="dark" className="w-10 h-10" />
            <span className="font-serif tracking-[0.15em] text-base md:text-lg font-light text-[#1F3A33] uppercase">
              The Catalyst Table
            </span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-xs font-semibold tracking-widest text-[#1F3A33] uppercase hover:text-[#C8A97E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Exit
          </Link>
        </div>
      </header>

      {/* Login Card Container */}
      <main className="flex-grow flex items-center justify-center py-16 px-6">
        <div className="w-full max-w-md bg-white border border-[#1F3A33]/15 p-8 md:p-10 shadow-none rounded-none text-left space-y-8">
          
          {/* Lock Icon and Wordmark */}
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-[#1F3A33]/5 text-[#C8A97E] flex items-center justify-center mx-auto">
              <Lock className="w-5 h-5 stroke-[1.5]" />
            </div>
            <h1 className="font-serif text-3xl font-light text-[#1F3A33]">Admin Portal</h1>
            <p className="text-xs tracking-widest text-[#8B6A4E] uppercase font-semibold">
              Authorized Committee Only
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border-l-2 border-red-800 text-xs text-red-800 font-sans">
                {error}
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-1 relative">
              <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="hello@thecatalysttable.com"
                  data-testid={LOGIN.emailInput}
                  className="bg-transparent border-0 border-b border-[#1F3A33]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#2E2E2E] placeholder:text-[#2E2E2E]/30 focus:ring-0 shadow-none outline-none transition-colors w-full pl-6"
                />
                <Mail className="w-4 h-4 text-[#1F3A33]/40 absolute left-0 bottom-3.5" />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-1 relative">
              <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">
                Security Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••••••"
                  data-testid={LOGIN.passwordInput}
                  className="bg-transparent border-0 border-b border-[#1F3A33]/30 focus:border-[#C8A97E] rounded-none px-0 py-3 text-[#2E2E2E] placeholder:text-[#2E2E2E]/30 focus:ring-0 shadow-none outline-none transition-colors w-full pl-6 pr-8"
                />
                <Lock className="w-4 h-4 text-[#1F3A33]/40 absolute left-0 bottom-3.5" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 bottom-3 text-[#1F3A33]/40 hover:text-[#1F3A33] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid={LOGIN.submitButton}
              className="w-full bg-[#1F3A33] text-[#F7F5F2] uppercase text-xs font-semibold tracking-[0.2em] py-4 hover:bg-[#2E2E2E] transition-all duration-300 disabled:opacity-50"
            >
              {submitting ? "Authenticating..." : "Access Control"}
            </button>
          </form>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1F3A33] text-[#F7F5F2] py-6 border-t border-[#C8A97E]/20 text-center text-xs">
        <p>© {new Date().getFullYear()} The Catalyst Table. Secure Access Port.</p>
      </footer>
    </div>
  );
}
